const NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';

const xml = (value) => String(value ?? '').replace(/[<>&"']/g, (char) => ({
  '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;',
}[char]));

const columnName = (number) => {
  let value = number + 1;
  let name = '';
  while (value) {
    const remainder = (value - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    value = Math.floor((value - 1) / 26);
  }
  return name;
};

const crc32 = (bytes) => {
  let crc = -1;
  for (let i = 0; i < bytes.length; i += 1) {
    crc ^= bytes[i];
    for (let bit = 0; bit < 8; bit += 1) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0);
  }
  return (crc ^ -1) >>> 0;
};

const put16 = (view, offset, value) => view.setUint16(offset, value, true);
const put32 = (view, offset, value) => view.setUint32(offset, value >>> 0, true);

const zipStore = (files) => {
  const encoder = new TextEncoder();
  const localParts = [];
  const centralParts = [];
  let localOffset = 0;

  files.forEach(([path, content]) => {
    const name = encoder.encode(path);
    const data = encoder.encode(content);
    const checksum = crc32(data);
    const local = new Uint8Array(30 + name.length + data.length);
    const localView = new DataView(local.buffer);
    put32(localView, 0, 0x04034b50);
    put16(localView, 4, 20);
    put16(localView, 6, 0x0800);
    put16(localView, 8, 0);
    put32(localView, 14, checksum);
    put32(localView, 18, data.length);
    put32(localView, 22, data.length);
    put16(localView, 26, name.length);
    local.set(name, 30);
    local.set(data, 30 + name.length);
    localParts.push(local);

    const central = new Uint8Array(46 + name.length);
    const centralView = new DataView(central.buffer);
    put32(centralView, 0, 0x02014b50);
    put16(centralView, 4, 20);
    put16(centralView, 6, 20);
    put16(centralView, 8, 0x0800);
    put16(centralView, 10, 0);
    put32(centralView, 16, checksum);
    put32(centralView, 20, data.length);
    put32(centralView, 24, data.length);
    put16(centralView, 28, name.length);
    put32(centralView, 42, localOffset);
    central.set(name, 46);
    centralParts.push(central);
    localOffset += local.length;
  });

  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  put32(endView, 0, 0x06054b50);
  put16(endView, 8, files.length);
  put16(endView, 10, files.length);
  put32(endView, 12, centralSize);
  put32(endView, 16, localOffset);

  return new Blob([...localParts, ...centralParts, end], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

const stylesXml = (colors) => {
  const font = (size, bold = false) => `<font><sz val="${size}"/><color rgb="FF000000"/><name val="Aptos Narrow"/>${bold ? '<b/>' : ''}</font>`;
  const border = '<border><left style="thin"><color rgb="FF000000"/></left><right style="thin"><color rgb="FF000000"/></right><top style="thin"><color rgb="FF000000"/></top><bottom style="thin"><color rgb="FF000000"/></bottom><diagonal/></border>';
  const centered = '<alignment horizontal="center" vertical="center" wrapText="1"/>';
  const shrinkCentered = '<alignment horizontal="center" vertical="center" shrinkToFit="1"/>';
  const xfs = [
    '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>',
    `<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyAlignment="1">${shrinkCentered}</xf>`,
    `<xf numFmtId="0" fontId="2" fillId="0" borderId="1" xfId="0" applyAlignment="1">${centered}</xf>`,
    ...colors.map((_, i) => `<xf numFmtId="0" fontId="3" fillId="${3 + i}" borderId="1" xfId="0" applyAlignment="1">${shrinkCentered}</xf>`),
    `<xf numFmtId="0" fontId="4" fillId="2" borderId="1" xfId="0" applyAlignment="1">${shrinkCentered}</xf>`,
    `<xf numFmtId="0" fontId="5" fillId="0" borderId="1" xfId="0" applyAlignment="1">${shrinkCentered}</xf>`,
    `<xf numFmtId="164" fontId="6" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyAlignment="1">${shrinkCentered}</xf>`,
    `<xf numFmtId="0" fontId="5" fillId="2" borderId="1" xfId="0" applyAlignment="1">${shrinkCentered}</xf>`,
  ].join('');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="${NS}"><numFmts count="1"><numFmt numFmtId="164" formatCode="$#,##0.00"/></numFmts><fonts count="7">${font(11)}${font(40, true)}${font(40, true)}${font(20, true)}${font(15, true)}${font(12, true)}${font(11)}</fonts><fills count="${3 + colors.length}"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FF808080"/><bgColor indexed="64"/></patternFill></fill>${colors.map((color) => `<fill><patternFill patternType="solid"><fgColor rgb="FF${color}"/><bgColor indexed="64"/></patternFill></fill>`).join('')}</fills><borders count="2"><border><left/><right/><top/><bottom/><diagonal/></border>${border}</borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="13">${xfs}</cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`;
};

export const makePriceWorkbook = ({ clientName, boxes, bulk, piece, sizes, getPrice, includeClientName = true }) => {
  const palette = ['A02B93', '00B0F0', '92D050', 'FFFF00', 'FFC000', 'FF0000'];
  const maxBoxGroups = Math.max(6, boxes.length);
  const boxEndCol = 1 + maxBoxGroups * 2 - 1;
  const lastCol = Math.max(14, boxEndCol);
  const rows = new Map();
  const merges = [];
  const rowHeights = new Map();
  const cell = (row, col, value = '', style = 9) => {
    if (!rows.has(row)) rows.set(row, new Map());
    rows.get(row).set(col, { value, style });
  };
  const merge = (row, startCol, endCol) => merges.push(`${columnName(startCol)}${row + 1}:${columnName(endCol)}${row + 1}`);
  const grid = (row, startCol, endCol, style = 10) => {
    for (let col = startCol; col <= endCol; col += 1) cell(row, col, '', style);
  };
  const section = (row, startCol, endCol, title) => {
    grid(row, startCol, endCol, 2);
    cell(row, startCol, title, 2);
    merge(row, startCol, endCol);
    rowHeights.set(row, 51.75);
  };
  const boxed = (value) => value === '' || value === null || value === undefined ? '' : Number.isFinite(Number(value)) ? Number(value) : '';

  const offset = includeClientName ? 1 : 0;
  if (includeClientName) {
    cell(0, 1, clientName, 1);
    merge(0, 1, lastCol);
    rowHeights.set(0, 51.75);
  }
  section(offset, 1, boxEndCol, 'CAJAS');
  const productHeadRow = offset + 1;
  const boxHeaderRow = offset + 2;
  const firstBrandRow = offset + 3;
  rowHeights.set(productHeadRow, 27);
  rowHeights.set(boxHeaderRow, 21);
  boxes.forEach((product, index) => {
    const startCol = 1 + index * 2;
    grid(productHeadRow, startCol, startCol + 1, 3 + (index % palette.length));
    cell(productHeadRow, startCol, product.name.toLocaleUpperCase('es'), 3 + (index % palette.length));
    merge(productHeadRow, startCol, startCol + 1);
    cell(boxHeaderRow, startCol, 'MARCA', 9);
    cell(boxHeaderRow, startCol + 1, 'PRECIO', 9);
  });
  if (boxes.length < 6) {
    const startCol = 1 + 5 * 2;
    grid(productHeadRow, startCol, startCol + 1, 8);
    merge(productHeadRow, startCol, startCol + 1);
    cell(boxHeaderRow, startCol, 'MARCA', 9);
    cell(boxHeaderRow, startCol + 1, 'PRECIO', 9);
  }
  const maxBrands = Math.max(15, ...boxes.map((product) => product.brands.length));
  for (let row = firstBrandRow; row < firstBrandRow + maxBrands; row += 1) grid(row, 1, boxEndCol, 10);
  for (let row = firstBrandRow; row < firstBrandRow + maxBrands; row += 1) rowHeights.set(row, 16.5);
  boxes.forEach((product, index) => {
    const startCol = 1 + index * 2;
    product.brands.forEach((brand, brandIndex) => {
      const row = firstBrandRow + brandIndex;
      cell(row, startCol, brand.toLocaleUpperCase('es'), 10);
      cell(row, startCol + 1, boxed(getPrice({ section: 'boxes', product: product.name, brand })), 11);
    });
  });

  const sectionRow = Math.max(19, firstBrandRow + maxBrands);
  section(sectionRow, 1, 8, 'GRANEL');
  section(sectionRow, 9, 14, 'POR PIEZA');
  const headersRow = sectionRow + 1;
  rowHeights.set(headersRow, 21);
  grid(headersRow, 1, 8, 9);
  cell(headersRow, 1, 'PRODUCTO', 9);
  cell(headersRow, 2, 'PRECIO', 9);
  grid(headersRow, 9, 14, 9);
  cell(headersRow, 9, 'PRODUCTO', 9);
  cell(headersRow, 10, 'PRECIO', 9);
  merge(headersRow, 10, 14);

  const firstDataRow = headersRow + 1;
  const dataCount = Math.max(bulk.length, piece.length + 1);
  for (let offset = 0; offset < dataCount; offset += 1) {
    const row = firstDataRow + offset;
    rowHeights.set(row, 16.5);
    grid(row, 1, 8, 10);
    grid(row, 9, 14, 10);
    if (bulk[offset]) {
      const product = bulk[offset];
      cell(row, 1, product.name.toLocaleUpperCase('es'), 10);
      cell(row, 2, boxed(getPrice({ section: 'bulk', product: product.name })), 11);
    }
  }
  let pieceRow = firstDataRow;
  piece.forEach((product) => {
    if (product.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() === 'res') {
      cell(pieceRow, 9, 'RES', 10);
      sizes.forEach((size, index) => cell(pieceRow, 10 + index, size.toLocaleUpperCase('es'), 12));
      pieceRow += 1;
      sizes.forEach((size, index) => cell(pieceRow, 10 + index, boxed(getPrice({ section: 'piece', product: product.name, variant: size })), 11));
      pieceRow += 1;
    } else {
      cell(pieceRow, 9, product.name.toLocaleUpperCase('es'), 10);
      cell(pieceRow, 10, boxed(getPrice({ section: 'piece', product: product.name })), 11);
      pieceRow += 1;
    }
  });

  const maxRow = Math.max(...rows.keys());
  const colWidths = Array.from({ length: lastCol + 1 }, (_, col) => {
    const templateWidths = [3, 17, 9.3, 12.5, 9.3, 11.4, 9.3, 13.3, 9.3, 13.2, 10.5, 10.5, 10.5, 10.5, 11.5];
    const width = templateWidths[col] || 11;
    return `<col min="${col + 1}" max="${col + 1}" width="${width}" customWidth="1"/>`;
  }).join('');
  const rowXml = Array.from({ length: maxRow + 1 }, (_, row) => {
    const cells = rows.get(row) || new Map();
    const body = [...cells.entries()].sort((a, b) => a[0] - b[0]).map(([col, item]) => {
      const ref = `${columnName(col)}${row + 1}`;
      if (typeof item.value === 'number') return `<c r="${ref}" s="${item.style}"><v>${item.value}</v></c>`;
      return `<c r="${ref}" s="${item.style}" t="inlineStr"><is><t xml:space="preserve">${xml(item.value)}</t></is></c>`;
    }).join('');
    const height = rowHeights.get(row);
    return `<row r="${row + 1}"${height ? ` ht="${height}" customHeight="1"` : ''}>${body}</row>`;
  }).join('');
  const mergeXml = merges.length ? `<mergeCells count="${merges.length}">${merges.map((range) => `<mergeCell ref="${range}"/>`).join('')}</mergeCells>` : '';
  const sheetXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="${NS}"><sheetPr><pageSetUpPr fitToPage="1"/></sheetPr><dimension ref="A1:${columnName(lastCol)}${maxRow + 1}"/><sheetViews><sheetView workbookViewId="0" showGridLines="0" zoomScale="100"/></sheetViews><sheetFormatPr defaultRowHeight="16.5"/><cols>${colWidths}</cols><sheetData>${rowXml}</sheetData>${mergeXml}<printOptions horizontalCentered="1"/><pageMargins left="0.25" right="0.25" top="0.5" bottom="0.5" header="0.2" footer="0.2"/><pageSetup paperSize="9" orientation="landscape" fitToWidth="1" fitToHeight="0"/></worksheet>`;
  const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="${NS}" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="FORMATO" sheetId="1" r:id="rId1"/></sheets></workbook>`;
  const relsXml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>';
  const rootRelsXml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>';
  const contentTypesXml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>';
  return zipStore([
    ['[Content_Types].xml', contentTypesXml], ['_rels/.rels', rootRelsXml], ['xl/workbook.xml', workbookXml],
    ['xl/_rels/workbook.xml.rels', relsXml], ['xl/styles.xml', stylesXml(palette)], ['xl/worksheets/sheet1.xml', sheetXml],
  ]);
};
